import { useMediaQuery } from "@mui/material";
import React, { useEffect, useRef, memo } from "react";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

export const PieChartPortfolio = ({ chartData }) => {
  const isSmall = useMediaQuery("(max-width:768px)");
  const COLORS = ["#384ef4", "#b060ed", "#16a34a", "#f59e0b"];
  const totalValue = chartData.reduce((acc, entry) => acc + entry.value, 0);

  // Formatter function for the legend text
  const renderLegendText = (value, entry) => {
    const { payload } = entry;
    const percentage = ((payload.value / totalValue) * 100).toFixed(0);
    return (
      <span>
        {value} ({percentage}%)
      </span>
    );
  };

  const renderCustomizedLabel = ({ name, value, percent }) => {
    // Option A: Clean $ with commas and 1 decimal place (Recommended)
    const formattedValue = value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });

    // Example: Ethereum ($139,958.2) 20%
    return `${formattedValue}`;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart className="mx-auto">
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={isSmall ? 100 : 110}
          fill="#b060ed"
          paddingAngle={5}
          dataKey="value"
          label={isSmall ? false : renderCustomizedLabel}
          className="mx-auto"
        >
          {chartData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend formatter={renderLegendText} />{" "}
      </PieChart>
    </ResponsiveContainer>
  );
};

// TradingViewWidget.jsx

export function TradingViewCrncyWidget({ currency = "BTC" }) {
  const container = useRef(null);

  useEffect(() => {
    // 1. Clear the container to prevent duplicates
    if (container.current) {
      container.current.innerHTML = "";
    }

    const script = document.createElement("script");
    script.id = "tradingview-widget-script"; // Give it an ID to track it
    script.src =
      "https://s3.tradingview.com/external-embedding/embed-widget-symbol-overview.js";
    script.type = "text/javascript";
    script.async = true;

    // Use JSON.stringify to keep the config clean
    script.innerHTML = JSON.stringify({
      symbols: [["BINANCE:" + currency.toUpperCase() + "USDT" + "|1D"]],
      chartOnly: false,
      width: "100%",
      height: "400", // Note: '100%' requires a parent with defined height
      locale: "en",
      colorTheme: "light",
      autosize: true,
      showVolume: false,
      hideDateRanges: false,
      hideMarketStatus: false,
      hideSymbolLogo: false,
      scalePosition: "right",
      scaleMode: "Normal",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
      fontSize: "10",
      headerFontSize: "small",
      noTimeScale: false,
      valuesTracking: "1",
      changeMode: "price-and-percent",
      chartType: "area",
      lineColor: "#2962FF",
      bottomColor: "rgba(41, 98, 255, 0)",
      topColor: "rgba(41, 98, 255, 0.3)",
      lineWidth: 2,
    });

    container.current.appendChild(script);

    // 2. Optional: Cleanup function if you want to be extra safe
    return () => {
      if (container.current) {
        container.current.innerHTML = "";
      }
    };
  }, [currency]); // Added currency as dependency so it refreshes on change

  return (
    <div
      className="tradingview-widget-container"
      ref={container}
      style={{ height: "100%", width: "100%" }}
    >
      <div className="tradingview-widget-container__widget"></div>
    </div>
  );
}

export const transformHoldingData = (apiResponse, findCurrency) => {
  if (!apiResponse) return [];

  // 1. Sort the result by totalValue in descending order
  const sortedData = [...apiResponse].sort(
    (a, b) => b.totalValue - a.totalValue,
  );

  // 2. Separate the top 4 holdings
  const top4Raw = sortedData.slice(0, 4);
  const remainingRaw = sortedData.slice(4);

  // 3. Format the top 4 for the chart
  const chartData = top4Raw.map((item) => ({
    // Capitalize the first letter (e.g., 'bitcoin' -> 'Bitcoin')
    name: findCurrency(item._id)?.symbol,
    value: parseFloat(item.totalValue.toFixed(2)), // Format to 2 decimal places
  }));

  // 4. If there are more than 4 coins, group the rest into "Others"
  if (remainingRaw.length > 0) {
    const othersValue = remainingRaw.reduce(
      (acc, item) => acc + item.totalValue,
      0,
    );
    chartData.push({
      name: "Others",
      value: parseFloat(othersValue.toFixed(2)),
    });
  }

  return chartData;
};
export default memo(TradingViewCrncyWidget);
